import { useEffect, useState } from 'react';
import { Button, InlineError } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import {
  createHuddleLink,
  getHuddleLink,
  huddleUsage,
  listHuddleLinks,
  revokeHuddleLink,
  type HuddleLinkRow,
} from './huddleLinks';

export function HuddleLinksPanel({
  sectionId,
  onLiveChange,
}: {
  sectionId: string;
  onLiveChange?: (token: string | null) => void;
}) {
  const { client } = useAuth();
  const [rows, setRows] = useState<HuddleLinkRow[]>([]);
  const [liveToken, setLiveToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  async function refresh() {
    const [nextRows, token] = await Promise.all([
      listHuddleLinks(client, sectionId),
      getHuddleLink(client, sectionId),
    ]);
    setRows(nextRows);
    setLiveToken(token);
    onLiveChange?.(token);
  }

  useEffect(() => {
    void refresh().catch(() => setError('The huddle link could not be loaded.'));
  }, [client, sectionId]);

  const live = rows.find((row) => row.is_live) ?? null;
  const url = liveToken === null ? null : `${window.location.origin}/huddle/${liveToken}`;

  async function create() {
    setPending(true);
    setError('');
    try {
      const token = await createHuddleLink(client, sectionId);
      setLiveToken(token);
      onLiveChange?.(token);
      await refresh();
    } catch {
      setError('The link was not created. Try again.');
    } finally {
      setPending(false);
    }
  }

  async function revoke() {
    setPending(true);
    setError('');
    try {
      await revokeHuddleLink(client, sectionId);
      setLiveToken(null);
      onLiveChange?.(null);
      await refresh();
    } catch {
      setError('The link was not turned off. Try again.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="huddle-link-panel" aria-label="Huddle link settings">
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <p className="huddle-link-state">
        <strong>{live === null ? 'Off' : 'On'}</strong>
        {live === null ? null : ` · ends ${live.token_tail}`}
      </p>
      {live === null ? null : <p className="huddle-link-usage">{huddleUsage(live)}</p>}

      {url === null ? null : (
        <div className="huddle-link-reveal" role="status">
          <label htmlFor={`huddle-link-${sectionId}`}>Active link</label>
          <input
            id={`huddle-link-${sectionId}`}
            readOnly
            value={url}
            onFocus={(event) => event.currentTarget.select()}
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void navigator.clipboard.writeText(url)}
          >
            Copy
          </Button>
        </div>
      )}

      <div className="huddle-link-actions">
        <Button size="sm" disabled={pending} onClick={() => void create()}>
          Replace link
        </Button>
        {live === null ? null : (
          <Button size="sm" variant="danger" disabled={pending} onClick={() => void revoke()}>
            Turn off
          </Button>
        )}
      </div>
      {live === null ? null : (
        <p className="huddle-link-replace-note">The old link stops working.</p>
      )}
    </div>
  );
}
